/**
 * publicLinkAdapter.js : payload du lien de séance → entrée de buildPeriodAnalysis (D247).
 *
 * Adaptateur unique : la page publique et son PDF lisent l'objet produit ici,
 * qui doit être égal, champ par champ, à celui de la fiche du propriétaire
 * pour la même séance et la même période (test de parité).
 *
 * Le serveur ne renvoie que les mesures de la période ; la fiche calcule sur
 * toute la séance. Les champs dérivés comblent l'écart sans champ brut :
 *   period.readingsTotal    → total (« N sur M ») et data_count du PDF ;
 *   period.medianIntervalMs → plafond des trous de la couverture ;
 *   bornes                  → union de la séance et de la période du lien,
 *     que le serveur a validée dans les vraies bornes (aucun rognage) ;
 *   acquisitionIntervalSec  → mobileMetadata.acquisitionInterval (pas des durées) ;
 *   estimatedTimestamps, externalSensor : lus tels quels par periodAnalysis et sensorLabels.
 */
import { buildPeriodAnalysis, sessionBounds } from '../period/periodAnalysis';

const toMs = (iso) => new Date(iso).getTime();

/** Séance de même forme que celle de la fiche, limitée aux champs reçus. */
export function adaptLinkedSession(payload, readings) {
  const { acquisitionIntervalSec, ...session } = payload.session;
  const total = payload.period ? payload.period.readingsTotal : readings.length;
  return {
    ...session,
    horseName: payload.horse?.horseName ?? null,
    data_count: total,
    ...(acquisitionIntervalSec != null ? { mobileMetadata: { acquisitionInterval: acquisitionIntervalSec } } : {}),
  };
}

/** Période du lien en ms epoch (D201), null pour la séance entière. */
export function linkWindow(payload) {
  return payload.period ? { start: toMs(payload.period.from), end: toMs(payload.period.to) } : null;
}

/** { session, readings, window, options } pour buildPeriodAnalysis. */
export function toAnalysisInput(payload, readings) {
  const list = readings || [];
  const session = adaptLinkedSession(payload, list);
  const window = linkWindow(payload);
  if (!window) return { session, readings: list, window: null, options: {} };
  const own = sessionBounds(session, list);
  const bounds = own
    ? { start: Math.min(own.start, window.start), end: Math.max(own.end, window.end) }
    : window;
  return {
    session, readings: list, window,
    options: { total: payload.period.readingsTotal, gapReferenceMs: payload.period.medianIntervalMs, bounds },
  };
}

/** Analyse de la page publique et de son PDF. */
export function buildLinkedAnalysis(payload, readings) {
  const { session, readings: list, window, options } = toAnalysisInput(payload, readings);
  return buildPeriodAnalysis(session, list, window, options);
}
